import { useState } from 'react'
import { Link, useParams } from 'react-router'
import {
  useDirectory,
  useProject,
  useProjectMembers,
  useTasks,
  useUpdateProject,
} from '../api/hooks'
import type { ProjectRole, ProjectStatus, Task, TaskStatus } from '../api/types'
import { QuickAdd } from '../components/QuickAdd'
import { TaskDrawer } from '../components/TaskDrawer'
import { TaskRow } from '../components/TaskRow'
import { formatDate, fromDateInputValue, toDateInputValue } from '../lib/dates'
import {
  Button,
  Chip,
  EmptyState,
  ErrorNotice,
  Loading,
  PROJECT_STATUS_LABEL,
  PageHeader,
  ProjectStatusPill,
  Stat,
  TabStrip,
} from '../components/ui'

/** Labels stay lowercase; the strip's `capitalize` is what renders them. */
const TABS = [
  { id: 'tasks', label: 'tasks' },
  { id: 'team', label: 'team' },
  { id: 'details', label: 'details' },
] as const

const STATUSES: ProjectStatus[] = ['ACTIVE', 'ON_HOLD', 'COMPLETED', 'ARCHIVED']
const ROLES: ProjectRole[] = ['LEAD', 'CONTRIBUTOR', 'VIEWER']
const OPEN_ORDER: TaskStatus[] = ['BLOCKED', 'REVIEW', 'IN_PROGRESS', 'TODO']

export function ProjectDetailPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const { data, isLoading, error } = useProject(projectId)
  const { data: tasks } = useTasks({ projectId, includeDone: true })
  const [openTaskId, setOpenTaskId] = useState<string | null>(null)
  const [tab, setTab] = useState<'tasks' | 'team' | 'details'>('tasks')

  if (isLoading) return <Loading />
  if (error) return <ErrorNotice error={error} />
  if (!data) return null

  const { project, permissions } = data

  const openTasks = (tasks ?? [])
    .filter((t) => t.status !== 'DONE')
    .sort((a, b) => OPEN_ORDER.indexOf(a.status) - OPEN_ORDER.indexOf(b.status))
  const doneTasks = (tasks ?? []).filter((t) => t.status === 'DONE')

  return (
    <>
      <nav className="mb-3 text-sm text-slate-500 dark:text-slate-400">
        <Link to="/projects" className="hover:underline">
          Projects
        </Link>
        <span className="mx-1.5">›</span>
        <span>{project.company.name}</span>
      </nav>

      <PageHeader
        title={project.name}
        subtitle={project.description ?? undefined}
        actions={<ProjectStatusPill status={project.status} />}
      />

      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Open" value={project.taskCounts.open} />
        <Stat
          label="Overdue"
          value={project.taskCounts.overdue}
          tone={project.taskCounts.overdue > 0 ? 'danger' : 'default'}
        />
        <Stat label="Total tasks" value={project.taskCounts.total} />
        <Stat label="Team" value={project.members.length} />
      </div>

      <TabStrip tabs={TABS} active={tab} onChange={setTab} />

      {tab === 'tasks' && (
        <>
          {permissions.canAddTasks ? (
            <QuickAdd defaultProjectId={project.id} placeholder="Add a task to this project…" />
          ) : (
            <p className="card mb-5 px-3 py-2 text-sm text-slate-600 dark:text-slate-400">
              {permissions.role === 'VIEWER'
                ? 'You have view-only access to this project.'
                : 'Ask the project lead to add you to this project before adding tasks.'}
            </p>
          )}

          <TaskList tasks={openTasks} onOpen={setOpenTaskId} emptyLabel="No open tasks." />

          {doneTasks.length > 0 && (
            <div className="mt-6">
              <h2 className="mb-2 px-1 text-sm font-semibold text-slate-500 dark:text-slate-400">
                Completed ({doneTasks.length})
              </h2>
              <TaskList tasks={doneTasks} onOpen={setOpenTaskId} emptyLabel="" />
            </div>
          )}
        </>
      )}

      {tab === 'team' && <TeamTab projectId={project.id} data={data} />}
      {tab === 'details' && <DetailsTab data={data} />}

      {openTaskId && <TaskDrawer taskId={openTaskId} onClose={() => setOpenTaskId(null)} />}
    </>
  )
}

function TaskList({
  tasks,
  onOpen,
  emptyLabel,
}: {
  tasks: Task[]
  onOpen: (id: string) => void
  emptyLabel: string
}) {
  if (tasks.length === 0) {
    return emptyLabel ? <EmptyState title={emptyLabel} /> : null
  }
  return (
    <div className="card overflow-hidden">
      {tasks.map((task) => (
        <TaskRow key={task.id} task={task} onOpen={onOpen} showProject={false} />
      ))}
    </div>
  )
}

function TeamTab({
  projectId,
  data,
}: {
  projectId: string
  data: NonNullable<ReturnType<typeof useProject>['data']>
}) {
  const { project, permissions } = data
  const { add, update, remove } = useProjectMembers(projectId)
  const { data: people } = useDirectory()
  const [userId, setUserId] = useState('')
  const [role, setRole] = useState<ProjectRole>('CONTRIBUTOR')

  const memberIds = new Set(project.members.map((m) => m.id))
  const candidates = (people ?? []).filter((p) => !memberIds.has(p.id))

  return (
    <div className="space-y-4">
      {permissions.canManage && (
        <form
          className="card flex flex-wrap items-end gap-2 p-3"
          onSubmit={(e) => {
            e.preventDefault()
            if (!userId) return
            add.mutate({ userId, role }, { onSuccess: () => setUserId('') })
          }}
        >
          <div className="min-w-48 flex-1">
            <label className="label" htmlFor="m-user">
              Add someone
            </label>
            <select
              id="m-user"
              className="field"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
            >
              <option value="">Choose a person…</option>
              {candidates.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label" htmlFor="m-role">
              Role
            </label>
            <select
              id="m-role"
              className="field"
              value={role}
              onChange={(e) => setRole(e.target.value as ProjectRole)}
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {roleLabel(r)}
                </option>
              ))}
            </select>
          </div>

          <Button type="submit" variant="primary" loading={add.isPending} disabled={!userId}>
            Add
          </Button>
        </form>
      )}

      {add.error && <ErrorNotice error={add.error} />}

      <div className="card divide-y divide-slate-100 dark:divide-slate-800">
        {project.members.length === 0 && (
          <p className="px-4 py-6 text-center text-sm text-slate-500 dark:text-slate-400">
            Nobody has been added to this project yet.
          </p>
        )}

        {project.members.map((member) => (
          <div key={member.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">
                {member.name}
                {!member.isActive && <span className="ml-2 text-xs text-slate-400">(inactive)</span>}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">{member.email}</p>
            </div>

            {permissions.canManage ? (
              <>
                <select
                  className="field max-w-40"
                  value={member.projectRole}
                  onChange={(e) =>
                    update.mutate({ userId: member.id, role: e.target.value as ProjectRole })
                  }
                  aria-label={`Role for ${member.name}`}
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {roleLabel(r)}
                    </option>
                  ))}
                </select>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => remove.mutate(member.id)}
                  loading={remove.isPending}
                >
                  Remove
                </Button>
              </>
            ) : (
              <Chip>{roleLabel(member.projectRole)}</Chip>
            )}
          </div>
        ))}
      </div>

      <p className="text-xs text-slate-500 dark:text-slate-400">
        Leads can manage the project and its team. Contributors can add and change tasks. Viewers can
        read and comment only. Everyone in {project.company.name} can see this project.
      </p>
    </div>
  )
}

function DetailsTab({ data }: { data: NonNullable<ReturnType<typeof useProject>['data']> }) {
  const { project, permissions } = data
  const updateProject = useUpdateProject()
  const { data: people } = useDirectory()

  const [name, setName] = useState(project.name)
  const [status, setStatus] = useState<ProjectStatus>(project.status)
  const [description, setDescription] = useState(project.description ?? '')
  const [leadUserId, setLeadUserId] = useState(project.lead?.id ?? '')
  const [startDate, setStartDate] = useState(toDateInputValue(project.startDate))
  const [targetDate, setTargetDate] = useState(toDateInputValue(project.targetDate))

  if (!permissions.canManage) {
    return (
      <dl className="card grid gap-3 p-4 text-sm sm:grid-cols-2">
        <Field label="Company" value={project.company.name} />
        <Field label="Status" value={PROJECT_STATUS_LABEL[project.status]} />
        <Field label="Lead" value={project.lead?.name ?? 'Not assigned'} />
        <Field label="Start" value={formatDate(project.startDate)} />
        <Field label="Target" value={formatDate(project.targetDate)} />
        <div className="sm:col-span-2">
          <Field label="Description" value={project.description ?? '—'} />
        </div>
        <p className="text-xs text-slate-500 sm:col-span-2 dark:text-slate-400">
          Only an administrator or the project lead can change these.
        </p>
      </dl>
    )
  }

  return (
    <form
      className="card space-y-3 p-4"
      onSubmit={(e) => {
        e.preventDefault()
        updateProject.mutate({
          id: project.id,
          name: name.trim(),
          status,
          description: description.trim() || null,
          leadUserId: leadUserId || null,
          startDate: fromDateInputValue(startDate),
          targetDate: fromDateInputValue(targetDate),
        })
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="label" htmlFor="d-name">
            Project name
          </label>
          <input id="d-name" className="field" value={name} onChange={(e) => setName(e.target.value)} />
        </div>

        <div>
          <label className="label" htmlFor="d-status">
            Status
          </label>
          <select
            id="d-status"
            className="field"
            value={status}
            onChange={(e) => setStatus(e.target.value as ProjectStatus)}
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {PROJECT_STATUS_LABEL[s]}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="label" htmlFor="d-lead">
            Lead
          </label>
          <select
            id="d-lead"
            className="field"
            value={leadUserId}
            onChange={(e) => setLeadUserId(e.target.value)}
          >
            <option value="">No lead</option>
            {people?.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="label" htmlFor="d-start">
              Start
            </label>
            <input
              id="d-start"
              type="date"
              className="field"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div>
            <label className="label" htmlFor="d-target">
              Target
            </label>
            <input
              id="d-target"
              type="date"
              className="field"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div>
        <label className="label" htmlFor="d-desc">
          Description
        </label>
        <textarea
          id="d-desc"
          className="field min-h-24"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      {updateProject.error && <ErrorNotice error={updateProject.error} />}
      {updateProject.isSuccess && !updateProject.isPending && (
        <p className="text-sm text-emerald-700 dark:text-emerald-400">Saved.</p>
      )}

      <Button type="submit" variant="primary" loading={updateProject.isPending}>
        Save changes
      </Button>
    </form>
  )
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="label">{label}</dt>
      <dd className="text-slate-800 dark:text-slate-200">{value}</dd>
    </div>
  )
}

function roleLabel(role: ProjectRole): string {
  return { LEAD: 'Lead', CONTRIBUTOR: 'Contributor', VIEWER: 'Viewer' }[role]
}
